import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, Building2, Users } from 'lucide-react';

const OPTIONS = [
  { label: 'Find projects', Icon: Briefcase, to: '/projects', testId: 'landing-audience-projects' },
  { label: 'Find brands', Icon: Building2, to: '/signup', testId: 'landing-audience-brands' },
  { label: 'Find creatives', Icon: Users, to: '/feed', testId: 'landing-audience-creatives' },
];

const CREATORS = [
  { name: 'Kirti Gupta', role: 'Model', img: '/creators/kirti-gupta-avatar.jpg', x: '14%', y: '38%', size: 56 },
  { name: 'Sehej Bhasin', role: 'Photographer', img: '/creators/sehej-bhasin-avatar.webp', x: '54%', y: '16%', size: 68 },
  { name: 'Gursimarh Anand', role: 'Social Media', img: '/creators/gursimarh-anand-avatar.jpg', x: '84%', y: '46%', size: 52 },
  { name: 'Varad Kulkarni', role: 'Cinematographer', img: '/creators/varad-kulkarni-avatar.jpg', x: '32%', y: '82%', size: 60 },
  { name: 'Gautam Jagdev', role: 'Fashion Creator', img: '/creators/gautam-jagdev-avatar.jpg', x: '76%', y: '84%', size: 54 },
];

export const AudienceSection = () => {
  const navigate = useNavigate();

  return (
    <section
      data-testid="landing-audience-section"
      className="relative py-24 md:py-32 bg-zinc-950 border-t border-zinc-900"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className="md:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-xs uppercase tracking-[0.2em] text-[#6C63F5] font-medium mb-5">
              Built for you
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tighter text-zinc-50">
              Who is Qreativs<br className="hidden md:block" /> for<span className="text-[#6C63F5]">?</span>
            </h2>
            <p className="mt-6 max-w-md text-zinc-400 text-lg leading-relaxed">
              Anyone looking to navigate their creative career — from the makers behind the work to the brands that back them.
            </p>

            <div className="mt-10 flex flex-col gap-3 max-w-sm">
              {OPTIONS.map(({ label, Icon, to, testId }) => (
                <button
                  key={label}
                  onClick={() => navigate(to)}
                  data-testid={testId}
                  className="landing-btn landing-btn-row group"
                >
                  <span className="inline-flex items-center gap-3">
                    <span className="landing-btn-row-icon">
                      <Icon size={15} />
                    </span>
                    {label}
                  </span>
                  <span className="landing-btn-row-arrow">
                    <ArrowRight size={14} />
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right — floating creator avatars over a soft globe */}
        <div className="md:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-square max-w-[520px] mx-auto"
          >
            {/* Concentric rings */}
            <div className="absolute inset-0 rounded-full border border-dashed border-zinc-700/70" />
            <div className="absolute inset-[14%] rounded-full border border-dashed border-zinc-700/60" />
            <div className="absolute inset-[28%] rounded-full border border-dashed border-zinc-700/50" />
            <div className="absolute inset-[42%] rounded-full border border-dashed border-[#6C63F5]/40" />
            <div className="absolute inset-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#6C63F5]/25 blur-2xl" />

            {/* Central Q mark */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-20 w-20 rounded-full bg-[#6C63F5] text-white font-display font-bold text-4xl flex items-center justify-center shadow-[0_16px_48px_rgba(108,99,245,0.55)] ring-4 ring-[#6C63F5]/20">
              Q
            </div>

            {/* Floating avatars */}
            {CREATORS.map((c, i) => (
              <div
                key={c.name}
                style={{ left: c.x, top: c.y, height: c.size, width: c.size }}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                title={`${c.name} — ${c.role}`}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.7 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full w-full"
                >
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4 + (i % 3), ease: 'easeInOut', repeat: Infinity, delay: i * 0.35 }}
                    className="h-full w-full rounded-full overflow-hidden ring-2 ring-zinc-950 shadow-[0_10px_30px_rgba(0,0,0,0.6)] hover:ring-[#6C63F5] transition-colors"
                  >
                    <img src={c.img} alt="" className="h-full w-full object-cover" loading="lazy" draggable={false} />
                  </motion.div>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;
